import React, { useState, useEffect } from 'react';
import { storeYearOfBirth } from '../utils/sessionStorage';
import { patientApi } from '../services/api'; // Import the API service
import { storeTokens } from '../utils/token'; // Import storeTokens
import { v4 as uuidv4 } from 'uuid'; // Import uuid for generating state

/**
 * YearOfBirthVerification component for verifying patient identity
 *
 * @param {Object} props - Component props
 * @param {string} props.shortUrl - The short URL from the initial link.
 * @param {Function} props.onSuccess - Callback function to call when verification is successful
 * @returns {JSX.Element} The year of birth verification component
 */
const YearOfBirthVerification = ({ shortUrl, onSuccess }) => { // Receive shortUrl prop
  // State for each digit of the year
  const [digit1, setDigit1] = useState('');
  const [digit2, setDigit2] = useState('');
  const [digit3, setDigit3] = useState('');
  const [digit4, setDigit4] = useState('');

  // Combined year of birth
  const yearOfBirth = `${digit1}${digit2}${digit3}${digit4}`;
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  // Removed isReturningUser and storedStep state as this logic is now in CheckInFlow.jsx

  // Removed useEffect for checking returning user as this is handled in CheckInFlow.jsx

  const handleSubmit = async (e) => { // Made function async
    e.preventDefault();

    // Basic validation
    if (!yearOfBirth) {
      setError('Please enter your year of birth');
      return;
    }

    // Check if it's a valid year format (4 digits)
    if (!/^\d{4}$/.test(yearOfBirth)) {
      setError('Please enter a valid 4-digit year');
      return;
    }

    // Check if shortUrl is available
    if (!shortUrl) {
        setError('Check-in link is missing. Please use the link provided in your notification.');
        return;
    }

    setIsLoading(true); // Start loading
    setError(''); // Clear previous errors

    try {
        // Generate a new random state for this request
        const newState = uuidv4();

        // Make the API call to initiate check-in and get tokens
        const response = await patientApi.initiateCheckInRequest({ // Using the correct function name
            short_url: shortUrl,
            yob: yearOfBirth,
            state: newState,
            // Include current time if backend expects it in the payload, otherwise backend uses request timestamp
            // current_time: new Date().toISOString(),
        });

        if (response.status === 200 && response.data.pin_jwt && response.data.refresh_token && response.data.state) {
            // API call successful - received tokens and state
            const { pin_jwt, refresh_token, state } = response.data;

            // The backend initiate_patient_checkin now returns the case_id
            const caseId = response.data.case_id;

            if (!caseId) {
                 setError('Case information missing in backend response.');
                 setIsLoading(false);
                 return;
            }

            // Store the received tokens and state
            storeTokens(pin_jwt, refresh_token, state, caseId);

            // Securely store the verified Year of Birth
            storeYearOfBirth(yearOfBirth, caseId); // Store YOB linked to caseId

            setIsVerified(true); // Mark as verified

            // Notify parent component of success, passing the received token data
            // The parent (CheckInFlow) will then decode the JWT and set tokenData state.
            onSuccess({ pin_jwt, refresh_token, state }); // Pass the received token data

        } else {
            // API call failed or returned unexpected data
            setError(response.data.error || 'Verification failed. Please try again.');
        }

    } catch (apiError) {
        console.error('API Error during YOB verification:', apiError);
        // Handle specific API errors (e.g., 401 for invalid YOB, 404 for invalid short URL)
        if (apiError.response) {
            if (apiError.response.status === 401) {
                setError('The year of birth you entered does not match our records. Please try again.');
            } else if (apiError.response.status === 404) {
                 setError('Check-in link not found or is invalid. Please use the link provided in your notification.');
            } else if (apiError.response.data && apiError.response.data.error === 'short_url_locked') {
                 setError('This check-in link has been disabled due to too many failed attempts. This is for your safety. Please contact Ankr Support at 888-488-ANKR (2657) or your clinic for assistance.');
            } else if (apiError.response.status === 400 && apiError.response.data && apiError.response.data.error) {
                 setError(apiError.response.data.error); // Display other backend validation errors
            }
            else {
                setError(`API error: ${apiError.response.status}. Please try again or contact support.`);
            }
        } else {
            setError('Looks like your internet is down? Please check your connection and try again in 1-2 minutes.');
        }
    } finally {
        setIsLoading(false); // Stop loading
    }
  };

  // Initialize feather icons after component mounts
  useEffect(() => {
    if (typeof window.feather !== 'undefined') {
      window.feather.replace();
    }
  }, []);


  return (
    <div className="card border-0">
      <div className="card-body row main-container">
        <div className='col-sm-6 col-12' style={{height:'40vh'}}>
        {/* <h3 className="card-title mb-4">Verify Your Identity</h3> */}

        {/* Removed returning user message as this is handled in CheckInFlow.jsx */}

        {!isVerified ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label className="form-label">
                Please enter your year of birth (YYYY)
              </label>
              <div className="custom-form-input-container">
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`form-control custom-form-input`}
                  style={{ border: error ? '1px solid var(--bs-form-invalid-color)' : undefined}}
                  id="digit1"
                  value={digit1}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      setDigit1(val);
                      if (val.length === 1 && document.getElementById('digit2')) {
                        document.getElementById('digit2').focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace
                    if (e.key === 'Backspace' && !digit1 && document.getElementById('digit1')) {
                      document.getElementById('digit1').focus();
                    }
                  }}
                  maxLength={1}
                  autoFocus
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`form-control custom-form-input`}
                  style={{ border: error ? '1px solid var(--bs-form-invalid-color)' : undefined}}
                  id="digit2"
                  value={digit2}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      setDigit2(val);
                      if (val.length === 1 && document.getElementById('digit3')) {
                        document.getElementById('digit3').focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace
                    if (e.key === 'Backspace' && !digit2 && document.getElementById('digit1')) {
                      document.getElementById('digit1').focus();
                    }
                  }}
                  maxLength={1}
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`form-control custom-form-input`}
                  style={{ border: error ? '1px solid var(--bs-form-invalid-color)' : undefined}}
                  id="digit3"
                  value={digit3}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      setDigit3(val);
                      if (val.length === 1 && document.getElementById('digit4')) {
                        document.getElementById('digit4').focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace
                    if (e.key === 'Backspace' && !digit3 && document.getElementById('digit2')) {
                      document.getElementById('digit2').focus();
                    }
                  }}
                  maxLength={1}
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`form-control custom-form-input`}
                  style={{ border: error ? '1px solid var(--bs-form-invalid-color)' : undefined}}
                  id="digit4"
                  value={digit4}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      setDigit4(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    // Handle backspace
                    if (e.key === 'Backspace' && !digit4 && document.getElementById('digit3')) {
                      document.getElementById('digit3').focus();
                    }
                  }}
                  maxLength={1}
                />
              </div>
              {error && <div className="invalid-feedback d-block fs-6 mt-2">{error}</div>}
            </div>

            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
          </form>

        ) : (
          <div className="text-center">
            <div className="alert alert-success mb-3">
              <i className="fas fa-check-circle me-2"></i>
              Identity verified successfully!
            </div>
            {/* Removed returning user message */}
            <div>
              Proceeding to the next step...
            </div>
          </div>
        )}
      </div>
      <div className="col-sm-6 col-12 mb-5 d-flex-inline justify-content-center">
        <img src="/dist/assets/images/ankr_mira.svg" alt="Ankr Mira" className="img-fluid" width='500px' style={{position:'absolute',right:0, bottom:0, zIndex:1}}  loading="lazy" />
        <img src="/dist/assets/images/ankr_mira_callout.svg" alt="Ankr Mira Callout" className="img-fluid" width='300px' style={{position:'absolute',right:0, bottom:-30, zIndex:2}} loading="lazy" />
        <img src="/dist/assets/images/ankr_mira_bg.svg" alt="Ankr Mira Bg" className="img-fluid" width='300px' style={{position:'absolute',right:0, bottom:0, zIndex:0}} loading="lazy" />
      </div>
      </div>
    </div>
  );
};

export default YearOfBirthVerification;